import { Fragment, useEffect, useMemo, useState } from 'react';
import { fmtText } from '@/utils/fmtText';
import instrumentsData from '@/data/instruments/index.json';
import styles from './MandateViewerPage.module.css';
import { PrintLink } from './PrintLink';
import { MandateReader2Col } from './MandateReader2Col';

const INSTRUMENTS = instrumentsData as Record<string, any>;
const ACT_COLORS: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };

// Resolve references cited inside an article's verbatim text.
// (crossRefs are not yet populated on instruments — L4 — so we derive referenced
// legislation and internal articles from the verbatim text, read-only.)
const ACT_ELI_BY_SIG: Record<string, string> = { amld: 'dir/2024/1640', amlr: 'reg/2024/1624', amlar: 'reg/2024/1620', tfr: 'reg/2023/1113' };
const ACT_TITLE_BY_SIG: Record<string, string> = { amld: 'Dir. (EU) 2024/1640', amlr: 'Reg. (EU) 2024/1624', amlar: 'Reg. (EU) 2024/1620', tfr: 'Reg. (EU) 2023/1113' };

type RefLeg = { act: string; art: number; eli: string; title: string };

function bodyOf(a: any): string {
  const t = a?.textEN || {};
  const tables = (t.refTables || [])
    .map((rt: any) => `${rt.heading}\n${(rt.entries || []).map((e: any) => `${e.term} ${e.text}`).join('\n')}`)
    .join('\n');
  const paras = (t.paragraphs || [])
    .map((p: any) => (p.heading ? `${p.heading}\n${p.text}` : p.text))
    .join('\n');
  return [tables, paras].filter(Boolean).join('\n');
}

// Referenced legislation comes from the curated structured crossRefs (L4),
// which point to base-act articles ({act, target:"art_N", type}).
function refLegOf(a: any): RefLeg[] {
  const out: RefLeg[] = [];
  for (const r of (a?.crossRefs || [])) {
    const sig = (r.act || '').toUpperCase();
    const art = parseInt(String(r.target).replace('art_', ''), 10);
    if (!ACT_ELI_BY_SIG[r.act] || !art) continue;
    out.push({ act: sig, art, eli: ACT_ELI_BY_SIG[r.act], title: ACT_TITLE_BY_SIG[r.act] });
  }
  return out;
}

// Internal "Article N of this Regulation" references are intentionally NOT in the
// structured crossRefs (they target the mandate's own articles, which are not
// modelled as cross-ref targets). Derive them from the verbatim text for the
// side-by-side "Referenced articles" navigation.
function selfRefsOf(a: any, selfNum: number): number[] {
  const body = bodyOf(a);
  const set = new Set<number>();
  const re = /Article\s+(\d+)\s+of\s+this\s+Regulation/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    const n = parseInt(m[1], 10);
    if (n !== selfNum) set.add(n);
  }
  return Array.from(set).sort((x, y) => x - y);
}

function sourceLine(inst: any) {
  const s = inst.sourcing || {};
  const eu = s.eurlex || {};
  if (eu.published && eu.eli) {
    const en = `https://eur-lex.europa.eu/eli/${eu.eli}/oj/eng`;
    const it = `https://eur-lex.europa.eu/eli/${eu.eli}/oj/ita`;
    return (
      <div className={styles.sources}>
        Official sources — EUR-Lex <a href={en} target="_blank" rel="noopener">EN ↗</a> &nbsp;
        <a href={it} target="_blank" rel="noopener">IT ↗</a>
      </div>
    );
  }
  const url = (s.amlaDocument && s.amlaDocument.url) || inst.documentUrl || 'https://www.amla.europa.eu/policy/regulatory-instruments_en';
  return (
    <div className={styles.sources}>
      Official source — AMLA <a href={url} target="_blank" rel="noopener">document ↗</a>{' '}
      <span className={styles.pend}>· EUR-Lex EN/IT once adopted</span>
    </div>
  );
}

type Props = { id: string; initialArt?: string };

// Standard-structure consultation-paper mandates (those carrying cpSections) use the
// new 2-column reader; everything else keeps the existing single-column reader.
export function MandateViewerPage({ id, initialArt }: Props) {
  const inst = INSTRUMENTS[id];
  if (inst && Array.isArray(inst.cpSections) && inst.cpSections.length > 0) {
    return <MandateReader2Col inst={inst} initialArt={initialArt} />;
  }
  return <MandateViewerLegacy id={id} />;
}

function MandateViewerLegacy({ id }: { id: string }) {
  const inst = INSTRUMENTS[id];
  const color = inst ? (ACT_COLORS[inst.actId] || '#333') : '#333';
  const [openArts, setOpenArts] = useState<number[]>([]);
  const [openRec, setOpenRec] = useState(false);
  const [openAnx, setOpenAnx] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (gid: string) => setOpenGroups(prev => {
    const next = new Set(prev);
    next.has(gid) ? next.delete(gid) : next.add(gid);
    return next;
  });

  useEffect(() => {
    if (inst) document.title = `${inst.type} ${inst.legalBasis} — ${inst.title}`;
  }, [inst]);

  const artByNum = (n: number) => inst.articles.find((a: any) => a.num === n);
  const titleByNum = useMemo(() => {
    const m: Record<number, string> = {};
    if (inst) for (const a of inst.articles) m[a.num] = a.title || '';
    return m;
  }, [inst]);

  if (!inst) {
    return <div style={{ padding: 40, fontFamily: 'system-ui' }}>Mandate not found: {id}</div>;
  }

  const openMain = (n: number) => setOpenArts([n]);
  const openSide = (n: number) => setOpenArts(prev => (prev.includes(n) ? prev : [...prev, n]));
  const closeAt = (i: number) => setOpenArts(prev => (i === 0 ? [] : prev.filter((_, idx) => idx !== i)));

  // Align titles to the same bottom line across the mandate + open article columns.
  useEffect(() => {
    const align = () => {
      const titles = Array.from(document.querySelectorAll<HTMLElement>(`.${styles.title}`));
      let max = 0;
      titles.forEach(t => { t.style.minHeight = '0px'; });
      titles.forEach(t => { if (t.offsetHeight > max) max = t.offsetHeight; });
      titles.forEach(t => { t.style.minHeight = max + 'px'; });
    };
    const raf = requestAnimationFrame(align);
    window.addEventListener('resize', align);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', align); };
  }, [openArts, openRec, openAnx, id]);

  const mainArt = openArts.length ? artByNum(openArts[0]) : null;
  const mainRefs = mainArt ? { refLeg: refLegOf(mainArt), selfRefs: selfRefsOf(mainArt, mainArt.num) } : null;

  function renderArticle(a: any, i: number) {
    const refs = { refLeg: refLegOf(a), selfRefs: selfRefsOf(a, a.num) };
    const verified = a?.textEN?.verified;
    const compact = i > 0;
    return (
      <div className={styles.col} key={`art-${a.num}-${i}`}>
        <button className={styles.colClose} onClick={() => closeAt(i)} aria-label="Close">✕</button>
        <h1 className={styles.title} style={{ color, borderBottomColor: color }}>Article {a.num} — {a.title}</h1>
        {sourceLine(inst)}
        {a.summary && (
          <div className={styles.summary} style={{ borderLeftColor: color }}>
            <strong>Summary</strong>
            <p>{a.summary}</p>
            {a.summary_disclaimer && <div className={styles.disclaimer}>{a.summary_disclaimer}</div>}
          </div>
        )}
        {!compact && (refs.refLeg.length > 0 || refs.selfRefs.length > 0) && (
          <>
            <div className={styles.sectionLabel}>Cross-references<span className={styles.rhint}>opens here</span></div>
            <div className={styles.refs}>
              {refs.refLeg.map(r => (
                <a key={`${r.act}-${r.art}`} className={styles.refBadge}
                   style={{ borderColor: ACT_COLORS[r.act.toLowerCase()] || '#333', color: ACT_COLORS[r.act.toLowerCase()] || '#333' }}
                   href={`https://eur-lex.europa.eu/eli/${r.eli}/art_${r.art}/oj/eng`} target="_blank" rel="noopener">
                  {r.act} Art.{r.art} <em>cited</em>
                </a>
              ))}
              {refs.selfRefs.map(n => (
                <a key={`self-${n}`} className={styles.refBadge} style={{ borderColor: color, color }} onClick={() => openMain(n)}>
                  Art.{n} <em>see-also</em>
                </a>
              ))}
            </div>
          </>
        )}
        {verified ? (
          <div className={styles.bannerOk}>✓ Full text taken verbatim from the official source ↗</div>
        ) : (
          <div className={styles.bannerDraft}>⚠ Draft text — verify against the official source</div>
        )}
        <div className={styles.sectionLabel}>Full text</div>
        {(a.textEN?.refTables || []).map((rt: any, ri: number) => (
          <div className={styles.refTable} key={`rt-${ri}`}>
            <div className={styles.paraHeading} style={{ color }}>{rt.heading}</div>
            <dl className={styles.refDl}>
              {(rt.entries || []).map((e: any, ei: number) => (
                <div className={styles.refRow} key={`rt-${ri}-${ei}`}>
                  <dt className={styles.refTerm} style={{ color }}>{e.term}</dt>
                  <dd className={styles.refDef}>
                    {e.text}
                    {(e.ojRef || e.eli) && (
                      <span className={styles.refSrc}>
                        {e.ojRef && <span>{e.ojRef}</span>}
                        {e.eli && <a href={e.eli} target="_blank" rel="noopener" style={{ color }}>ELI&nbsp;↗</a>}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        {(a.textEN?.paragraphs || []).map((p: any) => (
          <Fragment key={p.num}>
            {p.heading && <div className={styles.paraHeading} style={{ color }}>{p.heading}</div>}
            <div className={styles.para}>
              <span className={styles.paraNum} style={{ color }}>{p.num}.</span>
              <div className={styles.paraText} dangerouslySetInnerHTML={{ __html: fmtText(p.text) }} />
            </div>
          </Fragment>
        ))}
        <div className={styles.footer}>Always verify against the official source before relying on this text.</div>
      </div>
    );
  }

  const overflowCenter = openArts.length <= 1;

  return (
    <div className={styles.page}>
      <div className={`${styles.scroll}${overflowCenter ? ' ' + styles.center : ''}`}>
        {/* Mandate column (level 1) */}
        <div className={`${styles.col} ${styles.mcol}`}>
          <h1 className={styles.title} style={{ color, borderBottomColor: color }}>{inst.type} · {inst.legalBasis}</h1>
          <div className={styles.metaRow}>
            {sourceLine(inst)}
            <PrintLink accent={color} />
          </div>
          <div className={styles.summary} style={{ borderLeftColor: color }}>
            <strong>Mandate Summary</strong>
            <p>{inst.mandateSummary || inst.title}</p>
            {inst.mandateSummary_disclaimer && <div className={styles.disclaimer}>{inst.mandateSummary_disclaimer}</div>}
          </div>

          {inst.status === 'planned' && (
            <div className={styles.plannedBanner}>
              <span className={styles.pbIco}>🗓️</span>
              <div>
                <span className={styles.plannedPill}>Planned</span>
                {inst.schedule?.statutory && <span className={styles.etaPill}>statutory {inst.schedule.statutory.human}</span>}
                <br />
                <span style={{ display: 'inline-block', marginTop: 7 }}>
                  This standard is <strong>planned but not yet drafted</strong>: AMLA has not opened a public consultation, so no document is available yet.
                </span>
                <div className={styles.schedule}>
                  {inst.schedule?.statutory && (
                    <div className={styles.schedRow}>
                      <span className={styles.schedDot} style={{ background: '#b45309' }} />
                      <span className={styles.schedDate}>{inst.schedule.statutory.human}</span>
                      <span className={styles.schedWhat}>statutory deadline — AMLA to {inst.schedule.statutory.kind}</span>
                      <span className={styles.schedSrc}>{inst.schedule.statutory.source}</span>
                    </div>
                  )}
                  {inst.schedule?.consultation && (
                    <div className={styles.schedRow}>
                      <span className={styles.schedDot} style={{ background: '#475569' }} />
                      <span className={styles.schedDate}>{inst.schedule.consultation.quarter}</span>
                      <span className={styles.schedWhat}>planned consultation opens</span>
                      <span className={styles.schedSrc}>{inst.schedule.consultation.source}</span>
                    </div>
                  )}
                  {inst.schedule?.finalDraft && (
                    <div className={styles.schedRow}>
                      <span className={styles.schedDot} style={{ background: '#475569' }} />
                      <span className={styles.schedDate}>{inst.schedule.finalDraft.quarter}</span>
                      <span className={styles.schedWhat}>planned final draft</span>
                      <span className={styles.schedSrc}>{inst.schedule.finalDraft.source}</span>
                    </div>
                  )}
                </div>
                {inst.schedule?.statutory && inst.schedule?.consultation && (
                  <span className={styles.pbSrc}>
                    Note: AMLA's planned consultation ({inst.schedule.consultation.quarter}) falls <strong>after</strong> the statutory deadline ({inst.schedule.statutory.human}) set by {inst.schedule.statutory.source}. Both are shown as published.
                  </span>
                )}
              </div>
            </div>
          )}

          <button className={`${styles.sectionToggle}${openRec ? ' ' + styles.open : ''}`} onClick={() => setOpenRec(v => !v)}>
            <span className={styles.chev}>▸</span>Recitals (Whereas)<span className={styles.cnt}>{inst.recitals?.length ? inst.recitals.length : 'not yet captured'}</span>
          </button>
          {openRec && (
            inst.recitalGroups?.length
              ? <div className={styles.recitalGroups}>
                  {inst.recitalGroups.map((g: any) => {
                    const nums: number[] = g.recitalNums || [];
                    const range = nums.length > 1 ? `${nums[0]}\u2013${nums[nums.length - 1]}` : `${nums[0]}`;
                    const open = openGroups.has(g.id);
                    return (
                      <div className={styles.recitalGroupCard} key={g.id} style={{ borderLeft: `3px solid ${color}` }}>
                        <div className={styles.recitalGroupHeader}>
                          <span className={styles.recitalGroupTitle}>{g.title}</span>
                          <span className={styles.recitalGroupCount}>{nums.length} recitals</span>
                        </div>
                        {g.superSummary && <p className={styles.recitalGroupSummary}>{g.superSummary}</p>}
                        <div className={styles.recitalGroupFooter}>
                          <span className={styles.recitalGroupNums}>({range})</span>
                          <button className={styles.recitalReaderBtn} style={{ color, borderColor: color }} onClick={() => toggleGroup(g.id)}>
                            {open ? 'Hide recitals' : 'Read recitals'}
                          </button>
                        </div>
                        {g.summary_disclaimer && <span className={styles.recitalGroupDisclaimer}>{g.summary_disclaimer}</span>}
                        {open && (
                          <div className={styles.recitals} style={{ marginTop: 8 }}>
                            {nums.map((n: number) => {
                              const r = (inst.recitals || []).find((x: any) => x.num === n);
                              return r && (
                                <div className={styles.recital} key={n}>
                                  <span className={styles.recNum} style={{ color }}>({r.num})</span>
                                  <span className={styles.recText}>{r.text}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              : inst.recitals?.length
                ? <div className={styles.recitals}>
                    {inst.recitals.map((r: any) => (
                      <div className={styles.recital} key={r.num}>
                        <span className={styles.recNum} style={{ color }}>({r.num})</span>
                        <span className={styles.recText}>{r.text}</span>
                      </div>
                    ))}
                  </div>
                : <div className={styles.placeholder}>Mandate recitals (“Whereas …”) are not yet captured in the data.</div>
          )}

          <button className={`${styles.sectionToggle} ${styles.open}`} style={{ cursor: 'default' }}>
            <span className={styles.chev} style={{ transform: 'rotate(90deg)' }}>▸</span>Articles<span className={styles.cnt}>{inst.articles.length}</span>
          </button>
          <div className={styles.rowlist}>
            {inst.articles.map((a: any) => (
              <a key={a.num} className={styles.row} onClick={() => openMain(a.num)}
                 style={openArts[0] === a.num ? { background: '#eef5fc', boxShadow: `inset 2px 0 0 ${color}` } : undefined}>
                <span className={styles.n} style={{ color }}>{a.num}.</span>
                <span className={styles.t}>{a.title}</span>
                <span className={styles.go}>▸</span>
              </a>
            ))}
          </div>

          <button className={`${styles.sectionToggle}${openAnx ? ' ' + styles.open : ''}`} onClick={() => setOpenAnx(v => !v)}>
            <span className={styles.chev}>▸</span>Annexes<span className={styles.cnt}>{inst.annexes?.length ? inst.annexes.length : 'not yet captured'}</span>
          </button>
          {openAnx && (inst.annexes?.length
            ? <div className={styles.annexes}>
                {inst.annexes.map((ax: any) => (
                  <div className={styles.annex} key={ax.id}>
                    <div className={styles.annexTitle} style={{ color }}>{ax.title}</div>
                    {ax.isReference
                      ? <div className={styles.annexRef}>
                          📎 Template provided in a separate AMLA document — not reproduced here.{' '}
                          <a href={ax.source} target="_blank" rel="noopener">official document ↗</a>
                        </div>
                      : <pre className={styles.annexText}>{ax.text}</pre>}
                  </div>
                ))}
              </div>
            : <div className={styles.placeholder}>Annexes / templates are not yet captured in the data.</div>
          )}

          <div className={styles.footer}>Always verify against the official source.</div>
        </div>

        {/* Article columns (level 2) */}
        {openArts.map((n, i) => {
          const a = artByNum(n);
          return a ? renderArticle(a, i) : null;
        })}
      </div>

      {/* Right boxes (only for the main article) */}
      {mainArt && mainRefs && (
        <div className={styles.boxwrap}>
          {mainRefs.selfRefs.length > 0 && (
            <div className={styles.box}>
              <div className={styles.boxH} style={{ borderBottomColor: color, color }}>📑 Referenced articles<span className={styles.bhint}>side-by-side ⇆</span></div>
              <div className={styles.boxB}>
                {mainRefs.selfRefs.map(n => (
                  <button key={n} className={styles.cnItem} onClick={() => openSide(n)}>
                    <span className={styles.cnShort} style={{ color }}>Art. {n} <em>see-also</em></span>
                    <span className={styles.cnCode}>{titleByNum[n] || ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {mainRefs.refLeg.length > 0 && (
            <div className={styles.box}>
              <div className={styles.boxH} style={{ borderBottomColor: color, color }}>🏛 Referenced legislation<span className={styles.bhint}>new tab ↗</span></div>
              <div className={styles.boxB}>
                {mainRefs.refLeg.map(r => (
                  <a key={`${r.act}-${r.art}`} className={styles.cnItem}
                     href={`https://eur-lex.europa.eu/eli/${r.eli}/art_${r.art}/oj/eng`} target="_blank" rel="noopener">
                    <span className={styles.cnShort}>{r.act} Art. {r.art}</span>
                    <span className={styles.cnCode}>{r.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
